import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, SERVICE_OPTIONS, BEARING_TYPES, COLLECTION_METHODS } from "@/lib/api";
import { ArrowLeft, ArrowRight, Check, Package, Truck, CircleCheck, Upload, X } from "lucide-react";

const STEPS = ["Service", "Collection", "Bike", "You", "Review"];

const emptyForm = {
  service_type: "",
  collection_method: "",
  bike_make: "",
  bike_model: "",
  bike_year: "",
  bearing_types: [],
  notes: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  postcode: "",
  country: "United Kingdom",
};

export default function Booking() {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...emptyForm, service_type: location.state?.service || "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleBearing(name) {
    setForm((f) => ({
      ...f,
      bearing_types: f.bearing_types.includes(name)
        ? f.bearing_types.filter((b) => b !== name)
        : [...f.bearing_types, name],
    }));
  }

  function canNext() {
    if (step === 0) return !!form.service_type;
    if (step === 1) return !!form.collection_method;
    if (step === 2) return form.bike_make.trim() && form.bike_model.trim();
    if (step === 3)
      return (
        form.customer_name.trim() &&
        /\S+@\S+\.\S+/.test(form.customer_email) &&
        form.customer_phone.trim() &&
        form.address_line1.trim() &&
        form.city.trim() &&
        form.postcode.trim()
      );
    return true;
  }

  async function submit() {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", form);
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onUploadPhotos(files) {
    if (!files?.length || !result) return;
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(files).slice(0, 6 - photos.length)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post(
          `/bookings/${result.id}/photos?reference=${encodeURIComponent(result.reference)}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        uploaded.push(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Failed to upload ${file.name}`);
      }
    }
    setPhotos((prev) => [...prev, ...uploaded]);
    setUploading(false);
    if (uploaded.length) toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} uploaded.`);
  }

  if (result) {
    return (
      <div data-testid="booking-success" className="max-w-2xl mx-auto px-4 sm:px-8 py-16 sm:py-24 text-center">
        <CircleCheck size={64} className="text-orange-500 mx-auto mb-6" />
        <h1 className="font-display text-4xl sm:text-6xl uppercase mb-3">Booking Received</h1>
        <p className="text-zinc-300 mb-2">Your reference is</p>
        <div className="font-mono text-3xl text-orange-500 mb-6" data-testid="booking-reference">{result.reference}</div>
        <p className="text-zinc-400 mb-10">
          We've emailed a confirmation to <strong className="text-white">{result.customer_email}</strong>.
          Track this booking anytime at{" "}
          <Link to="/track" className="text-orange-500 underline">/track</Link>.
        </p>

        {/* Photo upload */}
        <div className="border border-white/10 bg-zinc-900/40 p-6 text-left mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Upload size={18} className="text-orange-500" />
            <h3 className="font-display uppercase text-lg">Attach bike or engine photos</h3>
          </div>
          <p className="text-sm text-zinc-400 mb-4">
            Optional — helps us quote more accurately. JPG / PNG / WEBP up to 8 MB, {6 - photos.length} left.
          </p>
          <label className="btn-hard inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-5 py-3 font-mono text-xs uppercase tracking-widest cursor-pointer">
            <Upload size={14} /> {uploading ? "Uploading..." : "Choose photos"}
            <input type="file" multiple accept="image/jpeg,image/png,image/webp" hidden
              disabled={uploading || photos.length >= 6}
              data-testid="photos-input"
              onChange={(e) => { onUploadPhotos(e.target.files); e.target.value = ""; }} />
          </label>
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-4">
              {photos.map((p) => (
                <div key={p.id} data-testid={`photo-thumb-${p.id}`} className="aspect-square bg-zinc-950 border border-white/10 flex items-center justify-center text-xs text-zinc-400 font-mono">
                  <Check size={20} className="text-emerald-500 mr-1" /> {p.original_filename.slice(0, 12)}
                </div>
              ))}
            </div>
          )}
        </div>

        <button onClick={() => navigate("/")} data-testid="booking-home-btn"
          className="btn-hard bg-orange-600 hover:bg-orange-500 text-white px-8 py-4 font-display uppercase tracking-widest text-sm">
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="max-w-4xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
      <div className="mb-10">
        <span className="font-mono text-xs uppercase tracking-widest text-orange-500">— Booking</span>
        <h1 className="font-display text-4xl sm:text-6xl uppercase mt-2">Start Your Rebuild</h1>
      </div>

      <div className="flex items-center justify-between mb-12 border-b border-white/10 pb-6">
        {STEPS.map((label, i) => (
          <div key={label} className="flex-1 flex items-center gap-2">
            <div
              data-testid={`step-indicator-${i}`}
              className={`w-8 h-8 flex items-center justify-center font-mono text-xs border ${
                i < step ? "bg-orange-600 border-orange-600 text-white" :
                i === step ? "border-orange-500 text-orange-500" : "border-white/20 text-zinc-500"
              }`}
            >
              {i < step ? <Check size={14} /> : i + 1}
            </div>
            <span className={`hidden sm:inline font-mono text-xs uppercase tracking-widest ${
              i === step ? "text-white" : "text-zinc-500"
            }`}>{label}</span>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-orange-500" : "bg-white/10"}`} />}
          </div>
        ))}
      </div>

      <div className="border border-white/10 bg-zinc-900/40 p-6 sm:p-10">
        {step === 0 && (
          <div data-testid="step-service">
            <h2 className="font-display uppercase text-2xl mb-6">What do you need?</h2>
            <div className="grid gap-3">
              {SERVICE_OPTIONS.map((s) => (
                <button key={s.value} data-testid={`service-option-${s.value}`} type="button"
                  onClick={() => set("service_type", s.value)}
                  className={`text-left border p-5 transition-all ${
                    form.service_type === s.value
                      ? "border-orange-500 bg-orange-600/10"
                      : "border-white/10 hover:border-white/30 bg-zinc-950/60"
                  }`}>
                  <div className="font-display uppercase text-xl">{s.label}</div>
                  <div className="text-sm text-zinc-400 mt-1">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="step-collection">
            <h2 className="font-display uppercase text-2xl mb-6">How should we collect?</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {COLLECTION_METHODS.map((c) => {
                const Icon = c.value === "courier_box" ? Package : Truck;
                return (
                  <button key={c.value} data-testid={`collection-option-${c.value}`} type="button"
                    onClick={() => set("collection_method", c.value)}
                    className={`text-left border p-6 transition-all ${
                      form.collection_method === c.value
                        ? "border-orange-500 bg-orange-600/10"
                        : "border-white/10 hover:border-white/30 bg-zinc-950/60"
                    }`}>
                    <Icon size={28} className="text-orange-500 mb-4" />
                    <div className="font-display uppercase text-lg">{c.label}</div>
                    <div className="text-sm text-zinc-400 mt-1">{c.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-bike" className="space-y-5">
            <h2 className="font-display uppercase text-2xl mb-4">Tell us about the bike</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Make" required value={form.bike_make} testId="bike-make" onChange={(v) => set("bike_make", v)} placeholder="e.g. Honda" />
              <Field label="Model" required value={form.bike_model} testId="bike-model" onChange={(v) => set("bike_model", v)} placeholder="e.g. CR250" />
              <Field label="Year" value={form.bike_year} testId="bike-year" onChange={(v) => set("bike_year", v)} placeholder="e.g. 1998" />
            </div>
            {form.service_type === "bearing_replacement" && (
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Which bearings?</label>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {BEARING_TYPES.map((b) => (
                    <label key={b} className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${
                      form.bearing_types.includes(b) ? "border-orange-500 bg-orange-600/10" : "border-white/10 bg-zinc-950/60"
                    }`}>
                      <input type="checkbox" checked={form.bearing_types.includes(b)} onChange={() => toggleBearing(b)}
                        data-testid={`bearing-${b.replace(/\s/g, "-").toLowerCase()}`}
                        className="accent-orange-600" />
                      <span className="text-sm">{b}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Notes (optional)</label>
              <textarea rows={4} data-testid="bike-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)}
                placeholder="Any known issues, requests, or context..."
                className="mt-2 w-full bg-zinc-950 border border-white/10 focus:border-orange-500 focus:ring-2 focus:ring-orange-600/30 outline-none px-4 py-3 text-white" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-customer" className="space-y-5">
            <h2 className="font-display uppercase text-2xl mb-4">Where should we send it?</h2>
            <Field label="Full name" required value={form.customer_name} testId="cust-name" onChange={(v) => set("customer_name", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email" required type="email" value={form.customer_email} testId="cust-email" onChange={(v) => set("customer_email", v)} />
              <Field label="Phone" required value={form.customer_phone} testId="cust-phone" onChange={(v) => set("customer_phone", v)} />
            </div>
            <Field label="Address line 1" required value={form.address_line1} testId="cust-addr1" onChange={(v) => set("address_line1", v)} />
            <Field label="Address line 2" value={form.address_line2} testId="cust-addr2" onChange={(v) => set("address_line2", v)} />
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="City" required value={form.city} testId="cust-city" onChange={(v) => set("city", v)} />
              <Field label="Postcode" required value={form.postcode} testId="cust-postcode" onChange={(v) => set("postcode", v)} />
              <Field label="Country" value={form.country} testId="cust-country" onChange={(v) => set("country", v)} />
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-review" className="space-y-5">
            <h2 className="font-display uppercase text-2xl mb-4">Review & submit</h2>
            <ReviewRow label="Service" value={SERVICE_OPTIONS.find((s) => s.value === form.service_type)?.label} />
            <ReviewRow label="Collection" value={COLLECTION_METHODS.find((c) => c.value === form.collection_method)?.label} />
            <ReviewRow label="Bike" value={`${form.bike_make} ${form.bike_model} ${form.bike_year}`.trim()} />
            {form.bearing_types.length > 0 && <ReviewRow label="Bearings" value={form.bearing_types.join(", ")} />}
            <ReviewRow label="Customer" value={`${form.customer_name} · ${form.customer_email}`} />
            <ReviewRow label="Address" value={`${form.address_line1}, ${form.city} ${form.postcode}`} />
            {form.notes && <ReviewRow label="Notes" value={form.notes} />}
            <p className="text-xs text-zinc-500 pt-4 border-t border-white/10">
              No payment taken — we'll follow up with a quote and next steps by email. You'll be
              able to add photos of the bike / engine on the next screen.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-8">
        <button type="button" disabled={step === 0} data-testid="booking-back-btn"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 font-mono text-xs uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed hover:border-orange-500">
          <ArrowLeft size={14} /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button type="button" data-testid="booking-next-btn" disabled={!canNext()}
            onClick={() => setStep((s) => s + 1)}
            className="btn-hard inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-8 py-3 font-mono text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:transform-none">
            Next <ArrowRight size={14} />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={submitting} data-testid="booking-submit-btn"
            className="btn-hard inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-8 py-3 font-mono text-xs uppercase tracking-widest disabled:opacity-40">
            {submitting ? "Submitting..." : "Submit Booking"} <Check size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">
        {label}{required && <span className="text-orange-500"> *</span>}
      </label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testId}
        className="mt-2 w-full bg-zinc-950 border border-white/10 focus:border-orange-500 focus:ring-2 focus:ring-orange-600/30 outline-none px-4 py-3 text-white" />
    </div>
  );
}

function ReviewRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-3 gap-4 border-b border-white/10 pb-3">
      <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="col-span-2 text-white">{value}</div>
    </div>
  );
}
